import React, {useMemo, useCallback} from 'react'
import {Grid} from '@mui/material'
import Button from '../../shared/UI/Button'
import EnhancementsReportHeader from './EnhancementsReportHeader'
import useSectionManager from './hooks/useSectionManager'
import {useEnhancementsReport} from './hooks/useEnhancementsReport'
import ReportContent from './ReportContent'
import {SectionConfig} from './types'
import {getEnhancementReportSections} from './EnhancementReportSections'
import {EnhancementFilterActions, EnhancementFilterData} from './EnhancementsReportFilter'

type EnhancementsReportHook = ReturnType<typeof useEnhancementsReport>

interface EnhancementsReportTabProps extends EnhancementFilterActions, EnhancementFilterData {
  enhancementsReportHook: EnhancementsReportHook;
}

function EnhancementsReportTab({
  enhancementsReportHook,
  ...filterProps
}: EnhancementsReportTabProps): React.ReactElement {
  const {
    showFilters,
    reportData,
    onTimeSubmissionData,
    productData,
    isLoading,
    hasSearched,
    currentFilters,
    requestTypeFilter,
    handleFilterClick,
    handleRefreshClick,
    handleFilterChange,
    handleSearch,
    handleTileClick,
  } = enhancementsReportHook

  const defaultSections = useMemo(() =>
      getEnhancementReportSections(
        reportData,
        onTimeSubmissionData,
        productData,
        currentFilters,
        requestTypeFilter,
        isLoading,
        handleTileClick
      ),
    [
      reportData,
      onTimeSubmissionData,
      productData,
      currentFilters,
      requestTypeFilter,
      isLoading,
      handleTileClick
    ]
  )

  const {
    isEditMode,
    hiddenSections,
    toggleEditMode,
    moveUp,
    moveDown,
    toggleVisibility,
    getVisibleSections,
    resetSections
  } = useSectionManager(defaultSections as SectionConfig[])
  
  const handleEditToggle = useCallback(() => {
    if (!hasSearched) {
      handleSearch()
    }
    toggleEditMode()
  }, [hasSearched, handleSearch, toggleEditMode])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <EnhancementsReportHeader
          onFilterClick={handleFilterClick}
          onRefreshClick={handleRefreshClick}
          showFilters={showFilters}
          {...filterProps}
          isEditMode={isEditMode}
          onToggleEditMode={handleEditToggle}
          onResetSections={resetSections}
          isLoading={isLoading}
        />
      </Grid>

      {showFilters && (
        <Grid item xs={12} display="flex" justifyContent="flex-end" sx={{mt: -3}}>
          <Button
            label="Search"
            type="button"
            onClick={handleSearch}
            loading={isLoading}
            dataTestId="EnhancementReporting-SearchBtn"
          />
        </Grid>
      )}

      {hasSearched && (
        <Grid item xs={12}>
          <ReportContent
            isLoading={isLoading}
            sections={getVisibleSections()}
            isEditMode={isEditMode}
            hiddenSections={hiddenSections}
            onMoveUp={moveUp}
            onMoveDown={moveDown}
            onToggleVisibility={toggleVisibility}
          />
        </Grid>
      )}
    </Grid>
  )
}

export default EnhancementsReportTab